import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Heart, Trash2, ExternalLink, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import axios from "axios";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const Wishlist = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, { withCredentials: true });
      setItems(response.data.items || []);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load wishlist");
    } finally {
      setIsLoading(false);
    }
  };

  const removeItem = async (itemId) => {
    setDeletingId(itemId);
    try {
      await axios.delete(`${API}/wishlist/${itemId}`, { withCredentials: true });
      setItems(prev => prev.filter(i => i.item_id !== itemId));
      toast.success("Item removed");
    } catch (error) {
      toast.error("Failed to remove item");
    } finally {
      setDeletingId(null);
    }
  };

  const trackClick = async (item) => {
    try {
      await axios.post(`${API}/track-click`, {
        product_url: item.url,
        affiliate_url: item.affiliate_url || item.url,
        source: item.source,
        color_hex: item.color_hex,
        category: item.category
      }, { withCredentials: true });
    } catch (error) {
      console.error("Failed to track click");
    }
  };

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    const cat = item.category || "Other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }} data-testid="wishlist-page">
      <main className="container py-12">
        <div className="max-w-6xl mx-auto">
          
          {/* Page Header */}
          <div className="mb-12 animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              <p className="text-meta uppercase tracking-widest" style={{ color: 'var(--accent-primary)' }}>
                Your Collection
              </p>
            </div>
            <h1 className="text-display mb-4" style={{ color: 'var(--text-primary)' }}>
              Saved Items
            </h1>
            <p className="text-body text-secondary max-w-lg">
              Items you've saved while exploring. Click to view or purchase.
            </p>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-shimmer w-32 h-1 rounded-full"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 animate-fade-in">
              <div 
                className="w-24 h-24 mx-auto mb-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--surface-secondary)' }}
              >
                <ShoppingBag className="w-10 h-10" style={{ color: 'var(--text-muted)' }} />
              </div>
              <h3 className="text-h3 mb-3" style={{ color: 'var(--text-primary)' }}>
                Your wishlist is empty
              </h3>
              <p className="text-body text-secondary mb-8 max-w-sm mx-auto">
                Save items while exploring to add them to your collection.
              </p>
              <Link to="/dashboard" className="btn-primary" data-testid="start-shopping-btn">
                Start Exploring
              </Link>
            </div>
          ) : (
            <div className="space-y-16 animate-fade-in">
              {Object.entries(groupedItems).map(([category, categoryItems]) => (
                <section key={category}>
                  {/* Category Header */}
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-8 h-px" style={{ backgroundColor: 'var(--accent-primary)' }}></div>
                    <h2 className="text-small uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
                      {category}
                    </h2>
                    <span className="text-meta" style={{ color: 'var(--accent-primary)' }}>
                      ({categoryItems.length})
                    </span>
                  </div>

                  {/* Items Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {categoryItems.map((item, index) => (
                      <div
                        key={item.item_id}
                        className="product-card group animate-fade-in"
                        style={{ animationDelay: `${index * 50}ms` }}
                        data-testid={`wishlist-item-${index}`}
                      >
                        {/* Image */}
                        <div className="product-card-image relative">
                          {item.image_url && (
                            <img src={item.image_url} alt={item.title} />
                          )}
                          
                          {/* Remove Button */}
                          <button
                            onClick={() => removeItem(item.item_id)}
                            disabled={deletingId === item.item_id}
                            className="absolute top-3 left-3 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                            style={{ backgroundColor: 'var(--surface-primary)', boxShadow: 'var(--shadow-sm)' }}
                            data-testid={`remove-item-${index}`}
                          >
                            {deletingId === item.item_id ? (
                              <div className="animate-shimmer w-4 h-4 rounded"></div>
                            ) : (
                              <Trash2 className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                            )}
                          </button>

                          {/* View Indicator */}
                          <div className="absolute top-3 right-3 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: 'var(--surface-primary)' }}>
                            <ExternalLink className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                          </div>

                          {/* Color Indicator */}
                          {item.color_hex && (
                            <div className="absolute bottom-3 left-3 flex items-center gap-2 px-2 py-1 rounded-md" style={{ backgroundColor: 'var(--surface-glass)', backdropFilter: 'blur(8px)' }}>
                              <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: item.color_hex, border: '1px solid var(--border-subtle)' }}
                              />
                              <span className="text-meta" style={{ color: 'var(--text-primary)' }}>
                                {item.color_hex}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="product-card-content">
                          <h4 className="text-small font-medium line-clamp-2 mb-2" style={{ color: 'var(--text-primary)' }}>
                            {item.title}
                          </h4>
                          <p className="text-meta text-muted mb-3">
                            Available at {item.source}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-body font-medium" style={{ color: 'var(--text-secondary)' }}>
                              {item.price}
                            </span>
                            <a
                              href={item.affiliate_url || item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackClick(item)}
                              className="text-small hover:underline" 
                              style={{ color: 'var(--accent-primary)' }}
                            >
                              View Item
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default Wishlist;
